import axios from 'axios';
import toast from 'react-hot-toast';
import { apiRateLimiter, csrfToken, sanitizeInput } from './security';
import { API_CONFIG } from '../config/api.config';

// Use API URL from config (supports proxy in development)
const API_URL = API_CONFIG.API_URL;

// Create axios instance with security configurations
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  // Include credentials for CORS
  withCredentials: true,
});

// Request interceptor with security enhancements
api.interceptors.request.use(
  (config) => {
    // Rate limiting check
    const rateLimitKey = `${config.method}-${config.url}`;
    if (!apiRateLimiter.isAllowed(rateLimitKey)) {
      toast.error('Too many requests. Please wait a moment.');
      return Promise.reject(new Error('Rate limit exceeded'));
    }

    // Add auth token from localStorage (plain token)
    const token = localStorage.getItem('token');
    
    if (token) {
      // Send the plain token with Bearer prefix
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing requests
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method.toUpperCase())) {
      const csrf = csrfToken.get();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }

    // Sanitize request data (skip for FormData and HTML content fields)
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      const htmlContentFields = ['content', 'NDA_TEXT', 'body', 'description', 'message', 'html', 'template_content'];
      const sanitizedData = {};
      for (const [key, value] of Object.entries(config.data)) {
        if (typeof value === 'string') {
          // Skip sanitization for HTML content fields
          if (htmlContentFields.includes(key)) {
            sanitizedData[key] = value; // Keep HTML as-is
          } else {
            sanitizedData[key] = sanitizeInput(value);
          }
        } else {
          sanitizedData[key] = value;
        }
      }
      config.data = sanitizedData;
    }

    // Add request timestamp for timeout tracking
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => {
    // Log response time
    const duration = new Date() - response.config.metadata.startTime;
    if (duration > 5000) {
    }

    // Update CSRF token if provided
    const newCsrfToken = response.headers['x-csrf-token'];
    if (newCsrfToken) {
      csrfToken.store(newCsrfToken);
    }

    return response;
  },
  (error) => {
    // Enhanced error handling
    if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    } else if (error.response) {
      switch (error.response.status) {
        case 401:
          // Unauthorized - clear auth and redirect
          localStorage.removeItem('token');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('user');
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
          break;
        case 403:
          toast.error('Access denied. You don\'t have permission for this action.');
          break;
        case 404:
          toast.error('Requested resource not found.');
          break;
        case 422:
          // Validation errors - will be handled by the calling function
          break;
        case 429:
          toast.error('Too many requests. Please wait before trying again.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          if (error.response.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error('An error occurred. Please try again.');
          }
      }
    } else if (error.request) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// Secure API methods with additional validation
export const secureApi = {
  get: (url, config = {}) => api.get(url, config),
  
  post: (url, data = {}, config = {}) => {
    // Skip validation for FormData
    if (data instanceof FormData) {
      return api.post(url, data, config);
    }
    
    // Validate data before sending
    if (data && typeof data === 'object') {
      const sanitizedData = {};
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string' && value.trim() === '') {
          continue; // Skip empty strings
        }
        sanitizedData[key] = value;
      }
      return api.post(url, sanitizedData, config);
    }
    return api.post(url, data, config);
  },
  
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  
  delete: (url, config = {}) => api.delete(url, config)
};

export default api;